import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    getDocs,
    query,
    orderBy,
    limit,
    where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Area,
    AreaChart,
} from "recharts";
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    CreditCard,
    Shield,
    Building2,
    Plus,
    Eye,
    Calendar,
    Target,
    ArrowUpRight,
    ArrowDownRight,
    Activity,
    Wallet,
    BarChart3,
    Settings,
    FileText,
    Bell,
    Calculator,
    Briefcase,
    Home,
    User,
    ChevronRight,
    Receipt,
    ShoppingCart,
    Car,
    Utensils,
    Heart,
    Tag,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
} from "lucide-react";

const XFinanceDashboard = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingExpenses, setLoadingExpenses] = useState(true);
    const [loadingPolicies, setLoadingPolicies] = useState(true);

    // Datos simulados para income/outcome
    const incomeOutcomeData = [
        { month: "Ene", income: 45000, outcome: 32000 },
        { month: "Feb", income: 48000, outcome: 35000 },
        { month: "Mar", income: 52000, outcome: 38000 },
        { month: "Abr", income: 47000, outcome: 36000 },
        { month: "May", income: 51000, outcome: 39000 },
        { month: "Jun", income: 55000, outcome: 42000 },
    ];

    const balanceData = [
        { month: "Ene", balance: 13000 },
        { month: "Feb", balance: 26000 },
        { month: "Mar", balance: 40000 },
        { month: "Abr", balance: 51000 },
        { month: "May", balance: 63000 },
        { month: "Jun", balance: 76000 },
    ];

    const iconMapping = {
        "ShoppingCart": ShoppingCart,
        "Car": Car,
        "Home": Home,
        "Utensils": Utensils,
        "Heart": Heart,
        "GraduationCap": Calendar,
        "Gamepad2": Activity,
        "Plane": TrendingUp,
        "PiggyBank": Wallet,
        "Tag": Tag,
    };

    useEffect(() => {
        if (user) {
            loadSubscriptions();
            loadExpenses();
            loadPolicies();
        }
    }, [user]);

    const loadSubscriptions = async () => {
        if (!user) return;

        setLoading(true);
        try {
            const subscriptionsRef = collection(db, "subscriptions", user.uid, "userSubscriptions");
            const q = query(subscriptionsRef, orderBy("createdAt", "desc"), limit(5));
            const querySnapshot = await getDocs(q);

            const subscriptionsData = [];
            querySnapshot.forEach((doc) => {
                subscriptionsData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setSubscriptions(subscriptionsData);
        } catch (error) {
            console.error("Error al cargar suscripciones:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadExpenses = async () => {
        if (!user) return;

        setLoadingExpenses(true);
        try {
            const expensesRef = collection(db, "expenses", user.uid, "userExpenses");
            const q = query(expensesRef, orderBy("createdAt", "desc"), limit(5));
            const querySnapshot = await getDocs(q);

            const expensesData = [];
            querySnapshot.forEach((doc) => {
                expensesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setExpenses(expensesData);
        } catch (error) {
            console.error("Error al cargar gastos:", error);
        } finally {
            setLoadingExpenses(false);
        }
    };

    const loadPolicies = async () => {
        if (!user) return;

        setLoadingPolicies(true);
        try {
            const policiesRef = collection(db, "policies");
            const q = query(
                policiesRef,
                where("userId", "==", user.uid),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(q);

            const policiesData = [];
            querySnapshot.forEach((doc) => {
                policiesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setPolicies(policiesData);
        } catch (error) {
            console.error("Error al cargar pólizas:", error);
        } finally {
            setLoadingPolicies(false);
        }
    };

    const getTotalMonthlySubscriptions = () => {
        return subscriptions
            .filter(sub => sub.activa)
            .reduce((total, sub) => {
                let monthlyAmount = sub.cantidad;
                switch (sub.frecuenciaPago) {
                    case "semanal":
                        monthlyAmount = sub.cantidad * 4.33;
                        break;
                    case "trimestral":
                        monthlyAmount = sub.cantidad / 3;
                        break;
                    case "semestral":
                        monthlyAmount = sub.cantidad / 6;
                        break;
                    case "anual":
                        monthlyAmount = sub.cantidad / 12;
                        break;
                }
                return total + monthlyAmount;
            }, 0);
    };

    const getTotalMonthlyExpenses = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        return expenses
            .filter(expense => {
                const expenseDate = new Date(expense.fecha);
                return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
            })
            .reduce((total, expense) => total + expense.cantidad, 0);
    };

    const getExpensesByCategory = () => {
        const categoryTotals = {};
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        expenses
            .filter(expense => {
                const expenseDate = new Date(expense.fecha);
                return expenseDate.getMonth() === currentMonth && expenseDate.getFullYear() === currentYear;
            })
            .forEach(expense => {
                if (categoryTotals[expense.categoria]) {
                    categoryTotals[expense.categoria].value += expense.cantidad;
                } else {
                    categoryTotals[expense.categoria] = {
                        name: expense.categoria,
                        value: expense.cantidad,
                        color: getRandomColor(),
                    };
                }
            });

        return Object.values(categoryTotals).slice(0, 5);
    };

    const getRandomColor = () => {
        const colors = ["#1e293b", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const getUpcomingPayments = () => {
        const today = new Date();
        return subscriptions
            .filter(sub => sub.activa)
            .filter(sub => {
                const paymentDate = new Date(sub.fechaProximoPago);
                const diffTime = paymentDate - today;
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays >= 0 && diffDays <= 7;
            }).length;
    };

    const getPoliciesSummary = () => {
        const summary = {
            total: policies.length,
            pendiente: policies.filter(p => p.estado === "pendiente").length,
            aprobada: policies.filter(p => p.estado === "aprobada").length,
            rechazada: policies.filter(p => p.estado === "rechazada").length,
            en_revision: policies.filter(p => p.estado === "en_revision").length,
        };
        return summary;
    };

    const getStatusBadge = (estado) => {
        const statusConfig = {
            pendiente: {
                icon: Clock,
                color: "bg-yellow-100 text-yellow-800 border-yellow-200",
                label: "Pendiente",
            },
            aprobada: {
                icon: CheckCircle,
                color: "bg-green-100 text-green-800 border-green-200",
                label: "Aprobada",
            },
            rechazada: {
                icon: XCircle,
                color: "bg-red-100 text-red-800 border-red-200",
                label: "Rechazada",
            },
            en_revision: {
                icon: AlertCircle,
                color: "bg-blue-100 text-blue-800 border-blue-200",
                label: "En Revisión",
            },
        };

        const config = statusConfig[estado] || statusConfig.pendiente;
        const Icon = config.icon;

        return (
            <span
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border ${config.color}`}
            >
                <Icon className="w-3 h-3" />
                {config.label}
            </span>
        );
    };

    const currentMonth = incomeOutcomeData[incomeOutcomeData.length - 1];
    const previousMonth = incomeOutcomeData[incomeOutcomeData.length - 2];
    const incomeChange = ((currentMonth.income - previousMonth.income) / previousMonth.income * 100).toFixed(1);
    const outcomeChange = ((currentMonth.outcome - previousMonth.outcome) / previousMonth.outcome * 100).toFixed(1);

    const quickActions = [
        {
            title: "Registrar Gasto",
            description: "Nuevo gasto o compra",
            icon: Receipt,
            color: "bg-red-100 text-red-600",
            action: () => navigate("/expenses/new"),
        },
        {
            title: "Nueva Suscripción",
            description: "Agregar servicio recurrente",
            icon: CreditCard,
            color: "bg-blue-100 text-blue-600",
            action: () => navigate("/subscriptions/new"),
        },
        {
            title: "Ver Inversiones",
            description: "Revisar portfolio",
            icon: TrendingUp,
            color: "bg-green-100 text-green-600",
            action: () => navigate("/investments"),
        },
        {
            title: "Configuración",
            description: "Ajustes de cuenta",
            icon: Settings,
            color: "bg-slate-100 text-slate-600",
            action: () => navigate("/settings"),
        },
    ];

    const expensesByCategory = getExpensesByCategory();
    const totalMonthlyExpenses = getTotalMonthlyExpenses();

    const statsCards = [
        {
            title: "Saldo Actual",
            value: `$${balanceData[balanceData.length - 1].balance.toLocaleString("es-MX")}`,
            change: "+12.5%",
            changeType: "positive",
            icon: Wallet,
            color: "bg-blue-600",
        },
        {
            title: "Ingresos",
            value: `$${currentMonth.income.toLocaleString("es-MX")}`,
            change: `${incomeChange >= 0 ? '+' : ''}${incomeChange}%`,
            changeType: incomeChange >= 0 ? "positive" : "negative",
            icon: TrendingUp,
            color: "bg-green-600",
        },
        {
            title: "Gastos del Mes",
            value: `$${totalMonthlyExpenses.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
            change: `${expenses.length} registrados`,
            changeType: "neutral",
            icon: Receipt,
            color: "bg-red-600",
            clickAction: () => navigate("/expenses"),
        },
        {
            title: "Suscripciones",
            value: `$${getTotalMonthlySubscriptions().toLocaleString("es-MX", { minimumFractionDigits: 2 })}`,
            change: `${subscriptions.filter(s => s.activa).length} activas`,
            changeType: "neutral",
            icon: CreditCard,
            color: "bg-purple-600",
            clickAction: () => navigate("/subscriptions"),
        },
    ];

    const recentExpenses = expenses.slice(0, 3);
    const recentSubscriptions = subscriptions.slice(0, 3);

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900">Vista General</h1>
                            <p className="text-slate-600 font-medium">
                                Resumen de su situación financiera actual
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Policies Summary */}
                {loadingPolicies ? (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
                        <div className="animate-pulse">
                            <div className="h-6 bg-slate-200 rounded w-1/4 mb-4"></div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="h-20 bg-slate-200 rounded-lg"></div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900">Mis Solicitudes de Pólizas</h2>
                                    <p className="text-sm text-slate-600">Resumen de tus solicitudes de seguros</p>
                                </div>
                            </div>
                            {policies.length > 0 && (
                                <button
                                    onClick={() => navigate("/polizas")}
                                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
                                >
                                    Ver todas
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {policies.length > 0 ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-600">Total</span>
                                            <FileText className="w-4 h-4 text-slate-400" />
                                        </div>
                                        <p className="text-2xl font-bold text-slate-900">{getPoliciesSummary().total}</p>
                                        <p className="text-xs text-slate-500 mt-1">Solicitudes</p>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-yellow-700">Pendientes</span>
                                            <Clock className="w-4 h-4 text-yellow-600" />
                                        </div>
                                        <p className="text-2xl font-bold text-yellow-800">{getPoliciesSummary().pendiente}</p>
                                        <p className="text-xs text-yellow-600 mt-1">En espera</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-green-700">Aprobadas</span>
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        </div>
                                        <p className="text-2xl font-bold text-green-800">{getPoliciesSummary().aprobada}</p>
                                        <p className="text-xs text-green-600 mt-1">Activas</p>
                                    </div>
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-blue-700">En Revisión</span>
                                            <AlertCircle className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <p className="text-2xl font-bold text-blue-800">{getPoliciesSummary().en_revision}</p>
                                        <p className="text-xs text-blue-600 mt-1">Procesando</p>
                                    </div>
                                </div>

                                {/* Recent Policies */}
                                <div className="border-t border-slate-200 pt-4">
                                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Solicitudes Recientes</h3>
                                    <div className="space-y-2">
                                        {policies.slice(0, 3).map((policy) => {
                                            const getPolicyTypeLabel = (tipo) => {
                                                const types = {
                                                    seguro_viajero: "Seguro de Viajero",
                                                    seguro_auto: "Seguro de Auto",
                                                    seguro_hogar: "Seguro de Hogar",
                                                    seguro_vida: "Seguro de Vida",
                                                    seguro_gmm: "Gastos Médicos Mayores",
                                                };
                                                return types[tipo] || tipo;
                                            };

                                            let policyDate = null;
                                            if (policy.createdAt) {
                                                if (policy.createdAt.toDate) {
                                                    policyDate = policy.createdAt.toDate();
                                                } else if (policy.createdAt.seconds) {
                                                    policyDate = new Date(policy.createdAt.seconds * 1000);
                                                } else if (policy.createdAt instanceof Date) {
                                                    policyDate = policy.createdAt;
                                                } else if (typeof policy.createdAt === 'string') {
                                                    policyDate = new Date(policy.createdAt);
                                                }
                                            }

                                            return (
                                                <div
                                                    key={policy.id}
                                                    className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors border border-slate-100"
                                                >
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className="w-10 h-10 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg flex items-center justify-center flex-shrink-0">
                                                            <Shield className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-semibold text-slate-900 text-sm truncate">
                                                                {getPolicyTypeLabel(policy.tipoPoliza)}
                                                            </p>
                                                            <p className="text-xs text-slate-500">
                                                                {policyDate
                                                                    ? policyDate.toLocaleDateString("es-MX", {
                                                                        day: "numeric",
                                                                        month: "short",
                                                                        year: "numeric",
                                                                    })
                                                                    : "Fecha no disponible"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {getStatusBadge(policy.estado)}
                                                        <button
                                                            onClick={() => navigate("/polizas")}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        >
                                                            <ChevronRight className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {policies.length > 3 && (
                                        <button
                                            onClick={() => navigate("/polizas")}
                                            className="w-full mt-3 text-sm text-blue-600 hover:text-blue-700 font-semibold py-2 hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                            Ver todas las solicitudes ({policies.length})
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Shield className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                <p className="text-slate-600 font-medium mb-2">No hay solicitudes de pólizas</p>
                                <p className="text-sm text-slate-500 mb-4">Comienza protegiendo lo que más te importa</p>
                                <button
                                    onClick={() => navigate("/polizas")}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    Solicitar Póliza
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {statsCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div
                                key={index}
                                className={`bg-white rounded-xl border border-slate-200 shadow-sm p-6 transition-all ${stat.clickAction ? 'cursor-pointer hover:shadow-md hover:border-slate-300' : ''
                                    }`}
                                onClick={stat.clickAction}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    {stat.changeType !== "neutral" && (
                                        <div className={`flex items-center gap-1 ${stat.changeType === "positive"
                                            ? "text-green-600"
                                            : "text-red-600"
                                            }`}>
                                            {stat.changeType === "positive" ? (
                                                <ArrowUpRight className="w-4 h-4" />
                                            ) : (
                                                <ArrowDownRight className="w-4 h-4" />
                                            )}
                                            <span className="text-sm font-semibold">{stat.change}</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900 mb-1">{stat.value}</p>
                                    <p className="text-slate-600 font-medium">{stat.title}</p>
                                    {stat.changeType === "neutral" && (
                                        <p className="text-sm text-slate-500 mt-1">{stat.change}</p>
                                    )}
                                </div>
                                {stat.clickAction && (
                                    <div className="mt-2 text-right">
                                        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Income vs Outcome Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-slate-900">Ingresos vs Gastos</h3>
                            <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                    <span className="text-slate-600 font-medium">Ingresos</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <span className="text-slate-600 font-medium">Gastos</span>
                                </div>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={incomeOutcomeData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="month"
                                    stroke="#64748b"
                                    fontSize={12}
                                    fontWeight={500}
                                />
                                <YAxis
                                    stroke="#64748b"
                                    fontSize={12}
                                    fontWeight={500}
                                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                                />
                                <Tooltip
                                    formatter={(value, name) => [
                                        `$${value.toLocaleString()}`,
                                        name === 'income' ? 'Ingresos' : 'Gastos'
                                    ]}
                                    labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                />
                                <Bar dataKey="income" fill="#10b981" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="outcome" fill="#ef4444" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Balance Growth Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-slate-900">Balance Mensual</h3>
                            <div className="flex items-center gap-2 text-green-600">
                                <ArrowUpRight className="w-4 h-4" />
                                <span className="text-sm font-semibold">+18.2%</span>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={balanceData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="month"
                                    stroke="#64748b"
                                    fontSize={12}
                                    fontWeight={500}
                                />
                                <YAxis
                                    stroke="#64748b"
                                    fontSize={12}
                                    fontWeight={500}
                                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                                />
                                <Tooltip
                                    formatter={(value) => [`$${value.toLocaleString()}`, 'Balance']}
                                    labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="balance"
                                    stroke="#1e293b"
                                    strokeWidth={3}
                                    fill="url(#colorGradient)"
                                />
                                <defs>
                                    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1e293b" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#1e293b" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Bottom Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Expenses by Category */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-slate-900">Gastos por Categoría</h3>
                            <button
                                onClick={() => navigate("/expenses")}
                                className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
                            >
                                Ver todos
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {expensesByCategory.length > 0 ? (
                            <>
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={expensesByCategory}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {expensesByCategory.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value) => [`$${value.toLocaleString()}`, 'Gasto']}
                                            contentStyle={{
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="space-y-2 mt-4">
                                    {expensesByCategory.map((category, index) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: category.color }}
                                                ></div>
                                                <span className="text-sm font-medium text-slate-700">{category.name}</span>
                                            </div>
                                            <span className="text-sm font-semibold text-slate-900">
                                                ${category.value.toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Receipt className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                <p className="text-slate-600 font-medium">No hay gastos este mes</p>
                                <button
                                    onClick={() => navigate("/expenses/new")}
                                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-2"
                                >
                                    Registrar primer gasto
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Recent Expenses */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-slate-900">Gastos Recientes</h3>
                            <button
                                onClick={() => navigate("/expenses")}
                                className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
                            >
                                Ver todos
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {loadingExpenses ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="animate-pulse">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                                            <div className="flex-1">
                                                <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                                                <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : recentExpenses.length > 0 ? (
                            <div className="space-y-4">
                                {recentExpenses.map((expense) => (
                                    <div key={expense.id} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                                <Receipt className="w-5 h-5 text-red-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold text-slate-900">{expense.descripcion}</p>
                                                <p className="text-sm text-slate-500">{expense.categoria}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-slate-900">
                                                ${expense.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                            </p>
                                            <p className="text-sm text-slate-500">
                                                {new Date(expense.fecha).toLocaleDateString("es-MX")}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Receipt className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                <p className="text-slate-600 font-medium">No hay gastos registrados</p>
                                <button
                                    onClick={() => navigate("/expenses/new")}
                                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-2"
                                >
                                    Registrar primer gasto
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Recent Subscriptions & Quick Actions Combined */}
                    <div className="space-y-6">
                        {/* Recent Subscriptions */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-slate-900">Suscripciones</h3>
                                <button
                                    onClick={() => navigate("/subscriptions")}
                                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
                                >
                                    Ver todas
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>

                            {loading ? (
                                <div className="space-y-4">
                                    {[1, 2].map((i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                                                <div className="flex-1">
                                                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                                                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : recentSubscriptions.length > 0 ? (
                                <div>
                                    <div className="space-y-3">
                                        {recentSubscriptions.slice(0, 2).map((subscription) => (
                                            <div
                                                key={subscription.id}
                                                className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <CreditCard className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-slate-900">{subscription.nombre}</p>
                                                        <p className="text-sm text-slate-500">{subscription.categoria}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-slate-900">
                                                        ${subscription.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                    </p>
                                                    <p className="text-sm text-slate-500 capitalize">{subscription.frecuenciaPago}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {getUpcomingPayments() > 0 && (
                                        <div className="mt-4">
                                            <div className="flex items-center gap-2 text-orange-800">
                                                <Bell className="w-4 h-4" />
                                                <span className="font-semibold text-sm">
                                                    {getUpcomingPayments()} pago{getUpcomingPayments() > 1 ? "s" : ""} próximo
                                                    {getUpcomingPayments() > 1 ? "s" : ""}
                                                </span>
                                            </div>
                                            <p className="text-orange-700 text-sm mt-1">En los próximos 7 días</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <CreditCard className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                                    <p className="text-slate-600 font-medium text-sm">No hay suscripciones</p>
                                    <button
                                        onClick={() => navigate("/subscriptions/new")}
                                        className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-1"
                                    >
                                        Agregar suscripción
                                    </button>
                                </div>
                            )}

                            <div className="text-center py-6">
                                <CreditCard className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                                <p className="text-slate-600 font-medium text-sm">No hay suscripciones</p>
                                <button
                                    onClick={() => navigate("/subscriptions/new")}
                                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm mt-1"
                                >
                                    Agregar suscripción
                                </button>
                            </div>

                        </div>

                        {/* Quick Actions */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <h3 className="text-lg font-bold text-slate-900 mb-4">Acciones Rápidas</h3>
                            <div className="space-y-2">
                                {quickActions.map((action, index) => {
                                    const Icon = action.icon;
                                    return (
                                        <button
                                            key={index}
                                            onClick={action.action}
                                            className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <div className={`w-8 h-8 ${action.color} rounded-lg flex items-center justify-center`}>
                                                <Icon className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-semibold text-slate-900 text-sm">{action.title}</p>
                                                <p className="text-xs text-slate-500">{action.description}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-400" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default XFinanceDashboard;